import React, { useMemo } from 'react';
import { Steps, Popover, Button, message, theme } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { ProjectProgressPopover } from './ProjectProgressPopover';
import { useProject } from '../hooks';
import { IPhase } from '../types';
import { useNavigate } from 'react-router';

const { useToken } = theme;

export const ProjectProgress: React.FC = () => {
  const { token } = useToken();
  const navigate = useNavigate();
  const { project, phases, activePhase, setActivePhase, loading } = useProject();

  const currentStep = useMemo(() => {
    const activeIndex = phases.findIndex(phase => phase.id === activePhase?.id || phase.isActive);
    return activeIndex === -1 ? 0 : activeIndex;
  }, [phases, activePhase?.id]);

  if (!phases.length) return null;

  const handleStepChange = async (current: number) => {
    const phase = phases[current];

    if (phase.locked) {
      message.info('These steps will be available later');
      return;
    }

    setActivePhase(phase.id);
    if (project) {
      navigate(`/project/${project.id}/phase/${phase.id}`);
    }
  };

  const renderStepTitle = (phase: IPhase, index: number) => {
    // Only show title for the active step
    if (index === currentStep) {
      return phase.name;
    }
    return null;
  };

  const getStepStatus = (phase: IPhase, index: number) => {
    if (index === currentStep && !loading) return 'process';
    if (phase.locked) return 'wait';
    // If not locked and not current, it's "unlocked" (completed or available)
    return 'finish';
  };

  const currentPhase = phases[currentStep];
  const currentPercent = currentPhase?.progress || 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
      <Steps
        current={currentStep}
        onChange={handleStepChange}
        size="default"
        type="navigation"
        labelPlacement="horizontal"
        percent={currentPercent}
        items={phases.map((phase, index) => ({
          title: renderStepTitle(phase, index),
          status: getStepStatus(phase, index),
          disabled: phase.locked,
        }))}
      />

      <Popover content={<ProjectProgressPopover />} title="Project Phases" trigger="click">
        <Button
          type="text"
          shape="circle"
          icon={<QuestionCircleOutlined style={{ color: token.colorTextSecondary }} />}
        />
      </Popover>
    </div>
  );
};
